/**
 * Flight legs are always ordered chronologically — by departure date, then
 * departure time — everywhere they are shown and everywhere they are stored.
 *
 * The flight form groups its legs by direction (all Outbound, then all Inbound),
 * which is the right shape for editing but the wrong one for reading: a round
 * trip with a connection would list BRU→IST, COV→IST, IST→COV, IST→BRU instead
 * of the order they are actually flown.
 *
 * Legs reach this module in two shapes — the booking-form shape (`date`/`time`)
 * and the API `productDetails` shape (`departureDate`/`departureTime`) — so the
 * comparator reads both. Dates arrive as ISO (2026-05-14) or dd.mm.yyyy.
 */

const toIsoDate = (value) => {
  if (!value) return "";
  const s = String(value).trim();
  const dmy = /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return "";
};

const toHhMm = (value) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value ?? "").trim());
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "00:00";
};

/* "2026-05-14T14:25" — directly string-comparable. Empty for a leg with no
   usable departure date (a half-filled leg in the form). */
const departureKey = (leg) => {
  const date = toIsoDate(leg?.departureDate || leg?.date);
  return date ? `${date}T${toHhMm(leg?.departureTime || leg?.time)}` : "";
};

/**
 * Legs in chronological order. Legs without a departure date yet keep their
 * relative order and sit at the end, so a leg being filled in doesn't jump
 * around the list. Returns a new array; the input is untouched.
 */
export const sortFlightLegs = (legs) => {
  if (!Array.isArray(legs)) return [];
  if (legs.length < 2) return legs;
  return [...legs].sort((a, b) => {
    const ka = departureKey(a);
    const kb = departureKey(b);
    if (!ka || !kb) return (ka ? 0 : 1) - (kb ? 0 : 1);
    return ka.localeCompare(kb);
  });
};

/* productDetails is an object on most endpoints but a JSON string on a few, so
   preserve whichever type came in. */
const sortDetailsLegs = (details) => {
  const isString = typeof details === "string";
  let parsed = details;
  if (isString) {
    try { parsed = JSON.parse(details); } catch { return details; }
  }
  if (!parsed || !Array.isArray(parsed.legs) || parsed.legs.length < 2) return details;
  const legs = sortFlightLegs(parsed.legs);
  if (legs.every((leg, i) => leg === parsed.legs[i])) return details;
  const next = { ...parsed, legs };
  return isString ? JSON.stringify(next) : next;
};

/**
 * A booking fetched from the API with every flight product's legs put in
 * chronological order — applied once at the read boundary so every tab reading
 * that booking (detail, itinerary, SSRs, cancellation) shows the same order,
 * including bookings stored before legs were sorted on save.
 */
export const withSortedFlightLegs = (booking) => {
  if (!booking || !Array.isArray(booking.products)) return booking;
  let changed = false;
  const products = booking.products.map((product) => {
    const details = sortDetailsLegs(product?.productDetails);
    if (details === product?.productDetails) return product;
    changed = true;
    return { ...product, productDetails: details };
  });
  return changed ? { ...booking, products } : booking;
};
